import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';
import {
  type FC,
  isValidElement,
  type KeyboardEventHandler,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useSelector} from 'react-redux';
import {useOutsideAlerter} from '~/common/FabricInvitation/EventCreateDesign/hooks/useOutsideAlerter';
import {getLoggedUserId} from '~/common/fabric/User/reducers/user';
import {useEventSave} from '~/OneStepCreateInvitation/hooks/useEventSave';
import {selectEventId} from '~/OneStepCreateInvitation/reducers/event/selector';
import {Button} from '~library/atoms/Button';
import {Input, type InputProps} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {EditLineIcon} from '~library/svg/EditLineIcon';
import {tanzanite} from '~libSass/base/_exports.sass';
import './CustomInputWithIcon.sass';

interface CustomInputWithIconProps extends Omit<InputProps, 'data-qa-id' | 'value' | 'ref'> {
  bold?: boolean;
  onClick?: () => void;
  dataQaId?: string;
  fullWidth?: boolean;
  padded?: boolean;
  onExitEditMode?: () => void;
  displayValueFn?: (value: string | number | boolean | ReactNode) => ReactNode;
  onExitEditModeValidation?: (value?: string | number | boolean | ReactNode) => boolean;
  showEditIcon?: boolean;
  centerIcon?: boolean;
  value?: ReactNode;
  ref?: React.RefObject<HTMLDivElement | null>;
}

export const CustomInputWithIcon: FC<CustomInputWithIconProps> = ({
  pristine,
  bold = false,
  onClick,
  variant = '',
  dataQaId = '',
  fullWidth,
  padded,
  onExitEditMode,
  displayValueFn,
  onExitEditModeValidation,
  showEditIcon = true,
  centerIcon,
  ref,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [editMode, setEditMode] = useState(false);
  const isLoggedIn = useSelector(getLoggedUserId);
  const eventId = useSelector(selectEventId);
  const {autoSave} = useEventSave();

  const exitEditMode = useCallback(() => {
    const valid = onExitEditModeValidation ? onExitEditModeValidation(props?.value) : true;
    if (valid) {
      onExitEditMode?.();
      setEditMode(false);
    }
  }, [onExitEditMode, onExitEditModeValidation, props?.value]);

  useOutsideAlerter(inputRef, exitEditMode);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const onEditClick = useCallback(async () => {
    if (!isLoggedIn) {
      autoSave();
    } else {
      if (!eventId) {
        autoSave();
      }

      setEditMode(true);
      // It's necessary a tiny timeout before triggering the focus in this situation
      setTimeout(() => inputRef.current?.focus(), 1);
    }
  }, [isLoggedIn, autoSave, eventId, inputRef]);

  const onClickHandler = useCallback(async () => {
    if (!isLoggedIn) {
      autoSave();
    } else {
      onClick?.();
    }
  }, [isLoggedIn, autoSave, onClick]);

  const onKeyDownHandler: KeyboardEventHandler = useCallback(
    (event) => {
      if (event.key === 'Tab' || event.key === 'Escape') {
        exitEditMode();
      }
    },
    [exitEditMode]
  );

  useEffect(() => {
    const sheet = document.querySelectorAll('.bottom-sheet')[0];
    if (sheet) {
      // in MW when this Select is loaded, it receives focus which opens the device keyboard and pushes this out of view.
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 350);
    }
  }, []);

  return (
    <>
      <div
        id={props.id}
        className={cx(
          'custom-input-with-icon__wrapper',
          {
            'custom-input-with-icon__wrapper--bold': bold,
            'custom-input-with-icon__wrapper--fullWidth': fullWidth,
            'custom-input-with-icon__wrapper--edit-mode': editMode,
            'custom-input-with-icon__wrapper--padded': padded,
            'custom-input-with-icon__wrapper--center-icon': centerIcon,
            error: props.error,
          },
          props.className
        )}
        ref={ref}
      >
        {/* Input does not accept ReactNode as a valid type for value, so we show this state if value is a ReactNode */}
        {!editMode || isValidElement(props.value) ? (
          <Button
            data-qa-id={dataQaId}
            data-testid="custom-input-edit-icon"
            className={cx('custom-input-button', {'custom-input-button--error': props.error})}
            variant="transparent"
            onClick={onClick ? onClickHandler : onEditClick}
            disableProcessing
          >
            <div
              className={cx({
                'inline-input': true,
                'inline-input--pristine': pristine || isEmpty(props.value),
              })}
            >
              <Typography
                // biome-ignore lint/style/noNestedTernary: existing error
                variant={variant === 'large' ? 'header2' : bold ? 'paragraph1' : 'paragraph2'}
                className="inline-input__text"
                color={props.value ? 'charcoal' : 'coin'}
              >
                {displayValueFn && props.value
                  ? displayValueFn(props.value)
                  : props.value || props.placeholder}
              </Typography>
              {showEditIcon && (
                <div
                  className={cx({
                    'inline-input__icon': true,
                    'inline-input__icon--large': variant === 'large',
                  })}
                  data-qa-id={`${dataQaId}-edit-icon`}
                >
                  <EditLineIcon ratio={variant === 'large' ? 0.75 : 0.6} color={tanzanite} />
                </div>
              )}
            </div>
          </Button>
        ) : (
          <Input
            {...props}
            data-qa-id={`${dataQaId}-input-box`}
            ref={inputRef}
            className="real-input"
            onKeyDown={onKeyDownHandler}
            placeholder={!editMode ? props?.placeholder : undefined}
            value={props.value as string | number | boolean}
          />
        )}
      </div>
      {!editMode && props.error && typeof props.error === 'string' && (
        <span className="input-error">{props.error}</span>
      )}
    </>
  );
};
